import { computed } from "vue"
import { createI18n } from "vue-i18n"
import { englishMessages } from "@/i18n/messages/en"
import { russianMessages } from "@/i18n/messages/ru"

export type AppLocale = "en" | "ru"

const LOCALE_STORAGE_KEY = "codexnow-locale"
export const DEFAULT_LOCALE: AppLocale = "ru"
const supportedLocales: AppLocale[] = ["ru", "en"]

function isLocale(value: string | null | undefined): value is AppLocale {
  return supportedLocales.includes(value as AppLocale)
}

function detectLocale(): AppLocale {
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (isLocale(saved)) return saved
  return DEFAULT_LOCALE
}

function exactMessageResolver(messages: unknown, path: string): string | null {
  if (!messages || typeof messages !== "object") return null
  const value = (messages as Record<string, unknown>)[path]
  return typeof value === "string" ? value : null
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { en: englishMessages, ru: russianMessages },
  messageResolver: exactMessageResolver,
  missingWarn: false,
  fallbackWarn: false,
})

export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  document.documentElement.lang = locale
}

export function useLocale() {
  const locale = computed(() => i18n.global.locale.value as AppLocale)
  return {
    locale,
    setLocale,
    toggleLocale: () => setLocale(locale.value === "en" ? "ru" : "en"),
  }
}

export function currentLocale(): AppLocale {
  return i18n.global.locale.value as AppLocale
}

export function translate(key: string, values?: Record<string, string | number>): string {
  return values ? i18n.global.t(key, values) : i18n.global.t(key)
}

export function translateValidation(code: string): string {
  if (currentLocale() === "en") return code
  const key = `validation.${code}`
  if (i18n.global.te(key)) return i18n.global.t(key)
  return code.replaceAll("_", " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase())
}

export function translateError(code: string, fallback: string): string {
  const key = `error.${code}`
  return currentLocale() === "ru" && i18n.global.te(key) ? i18n.global.t(key) : translate(fallback)
}

export function translateStatus(value: string | null | undefined): string {
  const normalized = (value ?? "UNKNOWN").replaceAll(" ", "_").toUpperCase()
  if (currentLocale() === "en") return normalized.replaceAll("_", " ")
  const key = `status.${normalized}`
  if (i18n.global.te(key)) return i18n.global.t(key)
  const roleKey = `role.${normalized}`
  if (i18n.global.te(roleKey)) return i18n.global.t(roleKey)
  return normalized.replaceAll("_", " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase())
}

export function translateRole(value: string | null | undefined): string {
  if (currentLocale() === "en") return (value ?? "").replaceAll("_", " ")
  const key = `role.${value ?? "CUSTOMER"}`
  return i18n.global.te(key) ? i18n.global.t(key) : value ?? ""
}

export function translateEnum(value: string | null | undefined): string {
  const normalized = (value ?? "UNKNOWN").replace(/([a-z0-9])([A-Z])/g, "$1_$2").replaceAll(" ", "_").toUpperCase()
  const key = `enum.${normalized}`
  if (i18n.global.te(key)) return i18n.global.t(key)
  return normalized.replaceAll("_", " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase())
}

document.documentElement.lang = i18n.global.locale.value
